import React, { useEffect, useState } from "react";
import { DataGrid, GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { ENDPOINTS, getApiUrl } from "./../../../common/endpoints";
import { Requests } from "./../../../common/requests";
import { Add, Edit, Delete, Visibility } from "@mui/icons-material";
import { day_month, IS_SHOW_IMPORT, ZodiacOptions } from "./../../../constants";
import Swal from "sweetalert2";
import { data_grid_styles, text_field_styles } from "../../../constants/style";
import { useSnackbar } from "notistack";
import data_zodiac from "./../../../data/zodiac/zodiac_list.json";
import TogglePublicBtn from "../../../components/TogglePublicBtn";
import { SketchPicker } from "react-color";
import ColorPicker from "./ColorPicker";
import QuillEditor from "../../../components/QuillEditor";

interface Zodiac {
  id?: string; // id is optional for new entries
  code: string;
  name: string;
  startDayOfYear: number;
  endDayOfYear: number;
  coordinatesHistory: string;
  personality: string;
  luckyColors: string[];
}

const ZodiacList: React.FC = () => {
  const [items, setItems] = useState<Zodiac[]>([]);
  const [open, setOpen] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState<Zodiac | null>(null);
  const [dataZodiac, setDataZodiac] = useState<Zodiac>({
    code: "",
    name: "",
    startDayOfYear: 0,
    endDayOfYear: 0,
    coordinatesHistory: "",
    personality: "",
    luckyColors: [],
  });
  const [isImporting, setIsImporting] = useState(false);
  const [displayColorPicker, setDisplayColorPicker] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const fetchitems = async () => {
    const apiUrl = getApiUrl(ENDPOINTS.GET_LIST_ZODIAC + "?limit=0");
    const data = await Requests.get(apiUrl);
    const items = data.sort(
      (a: any, b: any) =>
        ZodiacOptions.findIndex((item) => item.value === a.code) -
        ZodiacOptions.findIndex((item) => item.value === b.code)
    );
    setItems(items);
  };

  const handleOpen = (zodiac?: Zodiac) => {
    setDataZodiac(
      zodiac || {
        code: "",
        name: "",
        startDayOfYear: 0,
        endDayOfYear: 0,
        coordinatesHistory: "",
        personality: "",
        luckyColors: [],
      }
    );
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Detail dialog close
  const handleDetailsClose = () => {
    setDetailsDialog(false);
    setSelectedRow(null);
  };

  const handleDelete = async (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const apiUrl = getApiUrl(ENDPOINTS.DELETE_ZODIAC.replace(":id", id));
          await Requests.delete(apiUrl);
          fetchitems();
          Swal.fire("Deleted!", "Delete sucessfully.", "success");
        } catch (e) {
          Swal.fire("Deleted!", "Delete unsucessfully.", "error");
        }
      }
    });
  };
  const handleView = (row: Zodiac) => {
    setSelectedRow(row);
    setDetailsDialog(true);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    try {
      event.preventDefault();

      if (dataZodiac.id) {
        const apiUrl = getApiUrl(
          ENDPOINTS.UPDATE_ZODIAC.replace(":id", dataZodiac.id)
        );
        await Requests.put(apiUrl, {
          ...dataZodiac,
          name: dataZodiac.code,
        });
      } else {
        const apiUrl = getApiUrl(ENDPOINTS.ADD_ZODIAC);
        await Requests.post(apiUrl, {
          ...dataZodiac,
          name: dataZodiac.code,
        });
      }

      handleClose();
      fetchitems();
      enqueueSnackbar("Save successfully!", { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Save unsuccessfully!", { variant: "error" });
    }
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setDataZodiac((prev) => ({ ...prev, [name]: value }));
  };

  const onTogglePublish = async (id: string) => {
    try {
      const apiUrl = getApiUrl(
        ENDPOINTS.ZODIAC_TOGGLE_PUBLISH.replace(":id", id)
      );
      await Requests.patch(apiUrl);
      fetchitems();
      enqueueSnackbar("Toggle publish success!", { variant: "success" });
    } catch (e) {}
  };

  const columns: GridColDef[] = [
    { field: "code", headerName: "Sign", width: 150 },
    {
      field: "startDayOfYear",
      headerName: "Start Day of Year",
      width: 120,
      renderCell: (data) => {
        return (
          <Box>
            {data.value} ({day_month[data.value - 1]})
          </Box>
        );
      },
    },
    {
      field: "endDayOfYear",
      headerName: "End Day of Year",
      width: 120,
      renderCell: (data) => {
        return (
          <Box>
            {data.value} ({day_month[data.value - 1]})
          </Box>
        );
      },
    },
    // {
    //   field: "coordinatesHistory",
    //   headerName: "Coordinates History",
    //   width: 200,
    //   renderCell: (data) => {
    //     return <Box dangerouslySetInnerHTML={{ __html: data.value }}></Box>;
    //   },
    // },
    // {
    //   field: "personality",
    //   headerName: "Personality",
    //   width: 200,
    //   renderCell: (data) => {
    //     return <Box dangerouslySetInnerHTML={{ __html: data.value }}></Box>;
    //   },
    // },
    {
      field: "luckyColors",
      headerName: "Lucky colors",
      width: 200,
      renderCell: (data) => {
        return (
          <Box
            sx={{
              display: "flex",
              gap: "4px",
              alignItems: "center",
              lineHeight: "normal",
            }}
          >
            {data.value?.map((color: any) => (
              <Box
                sx={{
                  backgroundColor: color,
                  padding: "4px 8px",
                  cursor: "pointer",
                  color: "#fff",
                }}
              >
                {color}
              </Box>
            ))}
          </Box>
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 270,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<Edit />}
          label="Edit"
          onClick={() => handleOpen(params.row as Zodiac)}
        />,
        <GridActionsCellItem
          icon={<Delete />}
          label="Delete"
          onClick={() => handleDelete(params.row.id)}
        />,
        <GridActionsCellItem
          icon={<Visibility />}
          label="View"
          onClick={() => handleView(params.row)}
        />,
        <TogglePublicBtn
          isValidated={!!params.row?.ispublished}
          onToggle={() => {
            onTogglePublish(params.row?.id);
          }}
        />,
      ],
    },
  ];

  useEffect(() => {
    fetchitems();
  }, []);

  const onImportData = async () => {
    setIsImporting(true);
    const apiUrl = getApiUrl(ENDPOINTS.BULK_ZODIAC);
    try {
      const nb_pages = 10;
      const nbReq = data_zodiac.length / nb_pages;
      for (let i = 0; i <= nbReq; i++) {
        await Requests.post(
          apiUrl,
          data_zodiac.slice(i * 10, (i + 1) * nb_pages)
        );
      }
      fetchitems();
      enqueueSnackbar("Import successfully!", { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Import unsuccessfully!", { variant: "error" });
    }
    setIsImporting(false);
  };

  return (
    <Box m={2}>
      <Box
        sx={{
          paddingBottom: "10px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            color="primary"
            variant="h5"
            sx={{
              fontWeight: "bold",
            }}
            gutterBottom
          >
            Zodiac
          </Typography>
          <Box>
            <Button
              variant="contained"
              sx={{
                textTransform: "unset",
                marginRight: "10px",
              }}
              onClick={() => handleOpen()}
            >
              Add
            </Button>
            {IS_SHOW_IMPORT && (
              <Button
                variant="contained"
                color="primary"
                sx={{
                  textTransform: "unset",
                }}
                onClick={() => {
                  onImportData();
                }}
                disabled={isImporting}
              >
                {isImporting && (
                  <CircularProgress
                    size="30px"
                    sx={{
                      marginRight: "10px",
                      color: "#fff",
                    }}
                  />
                )}
                Import
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      <DataGrid
        rows={items}
        columns={columns}
        rowHeight={100}
        sx={{ ...data_grid_styles }}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        sx={{
          "& .MuiPaper-root": {
            width: "1200px",
            maxWidth: "none",
          },
        }}
      >
        <DialogTitle
          color="primary"
          sx={{
            fontWeight: "bold",
          }}
        >
          {dataZodiac.id ? "Edit Zodiac" : "Add Zodiac"}
        </DialogTitle>
        <DialogContent
          sx={{
            "& .quill": {
              flex: 1,
              height: "calc(100% - 60px)",
              borderRadius: "10px",
              overflow: "hidden",
              border: "1px solid #2A4365",
            },
            "& .ql-toolbar": {
              backgroundColor: "#F7FAFC",
              border: "none",
            },
            "& .ql-container": {
              height: "calc(100% - 50px)",
              border: "none",
            },
          }}
        >
          <form onSubmit={handleSubmit}>
            <TextField
              select
              name="code"
              label="Code"
              fullWidth
              value={dataZodiac.code}
              onChange={handleChange}
              required
              sx={{
                ...text_field_styles,
              }}
            >
              {ZodiacOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {/* <TextField
              select
              name="name"
              label="Name"
              fullWidth
              value={dataZodiac.name}
              onChange={handleChange}
              required
              sx={{
                ...text_field_styles,
              }}
            >
              {ZodiacOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField> */}
            <TextField
              name="startDayOfYear"
              label="Start Day of Year"
              type="number"
              fullWidth
              value={dataZodiac.startDayOfYear}
              onChange={handleChange}
              required
              sx={{
                ...text_field_styles,
              }}
            />
            <TextField
              name="endDayOfYear"
              label="End Day of Year"
              type="number"
              fullWidth
              value={dataZodiac.endDayOfYear}
              onChange={handleChange}
              required
              sx={{
                ...text_field_styles,
              }}
            />
            <Box
              sx={{
                paddingBottom: "10px",
              }}
            >
              <Typography
                component={"label"}
                sx={{
                  color: "#2A4365",
                  fontWeight: "bold",
                  fontSize: "12px",
                }}
              >
                Coordinates History
              </Typography>
              <QuillEditor
                value={dataZodiac.coordinatesHistory}
                onChange={(value) =>
                  setDataZodiac((prev) => ({
                    ...prev,
                    coordinatesHistory: value,
                  }))
                }
                style={{ height: "200px" }}
              />
            </Box>
            <Box
              sx={{
                paddingBottom: "10px",
              }}
            >
              <Typography
                component={"label"}
                sx={{
                  color: "#2A4365",
                  fontWeight: "bold",
                  fontSize: "12px",
                }}
              >
                Personality
              </Typography>
              <QuillEditor
                value={dataZodiac.personality}
                onChange={(value) =>
                  setDataZodiac((prev) => ({ ...prev, personality: value }))
                }
                style={{ height: "200px" }}
              />
            </Box>
            <Typography
              sx={{ color: "#2A4365", fontWeight: "bold", fontSize: "12px" }}
            >
              Lucky colors
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: "4px",
              }}
            >
              {dataZodiac?.luckyColors?.map((color, index) => {
                return (
                  <ColorPicker
                    onSave={(color: any) => {
                      setDataZodiac((prev) => ({
                        ...prev,
                        luckyColors: prev.luckyColors.map(
                          (prevColor, prevIndex) =>
                            prevIndex === index ? color : prevColor
                        ),
                      }));
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: color,
                        padding: "4px 8px",
                        cursor: "pointer",
                      }}
                    >
                      {color}
                    </Box>
                  </ColorPicker>
                );
              })}
              <ColorPicker
                onSave={(color: any) => {
                  setDataZodiac((prev) => ({
                    ...prev,
                    luckyColors: prev?.luckyColors
                      ? [...prev?.luckyColors, color]
                      : [color],
                  }));
                }}
              >
                <Button
                  onClick={() => {
                    setDisplayColorPicker(true);
                  }}
                  variant="contained"
                  color="primary"
                  sx={{
                    textTransform: "unset",
                  }}
                >
                  Add color
                </Button>
              </ColorPicker>
            </Box>
            <DialogActions>
              <Button
                variant="contained"
                color="secondary"
                sx={{ textTransform: "unset" }}
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{ textTransform: "unset" }}
                type="submit"
              >
                {dataZodiac.id ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
      {/* Details Dialog */}
      {selectedRow && (
        <Dialog
          open={detailsDialog}
          onClose={handleDetailsClose}
          sx={{
            "& .ql-toolbar": {
              display: "none",
            },
          }}
        >
          <DialogTitle
            color="primary"
            sx={{
              fontWeight: "bold",
            }}
          >
            Zodiac Details
          </DialogTitle>
          <DialogContent>
            <Typography
              sx={{
                fontSize: "14px",
              }}
            >
              <strong>Name</strong>: {selectedRow.name}
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
              }}
            >
              <strong>startDayOfYear</strong>: {selectedRow.startDayOfYear}
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
              }}
            >
              <strong>endDayOfYear</strong>: {selectedRow.endDayOfYear}
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
              }}
            >
              <strong>Coordinates History</strong>:
            </Typography>
            <ReactQuill
              value={selectedRow.coordinatesHistory}
              readOnly={true}
              theme="snow"
            />
            <Typography
              sx={{
                fontSize: "14px",
              }}
            >
              <strong>personality</strong>:
            </Typography>
            <ReactQuill
              value={selectedRow.personality}
              readOnly={true}
              theme="snow"
            />
            <Typography
              sx={{
                fontSize: "14px",
              }}
            >
              <strong>Lucky colors</strong>: {selectedRow.luckyColors}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDetailsClose} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ZodiacList;
